import React, { useMemo } from "react";
import { map } from "lodash";
import styled from "styled-components";
import { FontWeight, prettyScrollbars } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import Markdown from "@ui-kit/markdown";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import {
  AISummaries,
  FailureReasons,
  SubAnalysisSummary,
  isAudienceAnalysis,
  isFailedSubAnalysisSummary,
  isGeneralAnalysis,
  isRiskAnalysis,
  isSignalAnalysis,
  isSucessfulSubAnalysisSummary,
} from "@radarly/models/ai";
import { DatavizTypeAI } from "@radarly/models/dataviz";
import Empty from "../empty";

interface SharedWidgetsAIInnerProps {
  aiType: DatavizTypeAI;
  data: AISummaries;
}

const Root = styled.div`
  height: 100%;
  ${prettyScrollbars};
  font-size: 16px;
  line-height: 22px;
  word-break: break-word;
`;

const Title = styled.h3`
  font-weight: ${FontWeight.Bold};
  &:not(:first-child) {
    margin-top: 22px;
  }
`;

const getLabelFromSubAnalysisSummary = (
  subAnalysis: SubAnalysisSummary,
  locale: Locale
) => {
  if (!subAnalysis || subAnalysis?.success === undefined) return null; // Only for general analysis - negative analysis, we don't display it
  if (isSucessfulSubAnalysisSummary(subAnalysis)) {
    return subAnalysis.summary;
  }
  if (isFailedSubAnalysisSummary(subAnalysis)) {
    if (subAnalysis.reason === FailureReasons.AIFailure) {
      return tr(
        locale,
        "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::AI_CARD_SERVICE_ERROR"
      );
    }
    if (subAnalysis.reason === FailureReasons.NotEnoughData) {
      return tr(locale, "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::AI_CARD_NO_DATA");
    }
  }
};

function getObjectFromAiType(
  data: AISummaries,
  aiType: DatavizTypeAI,
  locale: Locale
) {
  if (isGeneralAnalysis(data, aiType)) {
    return {
      GENERAL_ANALYSIS_LATEST: getLabelFromSubAnalysisSummary(
        data.latestNews,
        locale
      ),
      GENERAL_ANALYSIS_MOST_VISIBLE: getLabelFromSubAnalysisSummary(
        data.topReach,
        locale
      ),
      GENERAL_ANALYSIS_MOST_ENGAGING: getLabelFromSubAnalysisSummary(
        data.topEngagement,
        locale
      ),
      GENERAL_ANALYSIS_NEGATIVE_SENTIMENT: getLabelFromSubAnalysisSummary(
        data.negativeAnalysis,
        locale
      ),
    };
  }
  if (isSignalAnalysis(data, aiType)) {
    return {
      SIGNAL_ANALYSIS_HASHTAG: getLabelFromSubAnalysisSummary(
        data.hashtags,
        locale
      ),
      SIGNAL_ANALYSIS_EMOTIONS: getLabelFromSubAnalysisSummary(
        data.emotions,
        locale
      ),
      SIGNAL_ANALYSIS_AFFECTS: getLabelFromSubAnalysisSummary(
        data.affects,
        locale
      ),
      SIGNAL_ANALYSIS_MENTIONS: getLabelFromSubAnalysisSummary(
        data.mentions,
        locale
      ),
    };
  }

  if (isRiskAnalysis(data, aiType)) {
    return {
      RISK_ANALYSIS_LATEST_NEGATIVE: getLabelFromSubAnalysisSummary(
        data.latestNews,
        locale
      ),
      RISK_ANALYSIS_MOST_VISIBLE_NEGATIVE: getLabelFromSubAnalysisSummary(
        data.mostVisible,
        locale
      ),
      RISK_ANALYSIS_MOST_ENGAGING_NEGATIVE: getLabelFromSubAnalysisSummary(
        data.mostEngaging,
        locale
      ),
      RISK_ANALYSIS_MOST_EMERGING_NEGATIVE: getLabelFromSubAnalysisSummary(
        data.emerging,
        locale
      ),
    };
  }
  if (isAudienceAnalysis(data, aiType)) {
    return {
      AUDIENCE_ANALYSIS_COUNTRY: getLabelFromSubAnalysisSummary(
        data.countries,
        locale
      ),
      AUDIENCE_ANALYSIS_OCCUPATIONS: getLabelFromSubAnalysisSummary(
        data.occupations,
        locale
      ),
      AUDIENCE_ANALYSIS_TOP_AUTHORS: getLabelFromSubAnalysisSummary(
        data.users,
        locale
      ),
    };
  }
  return [];
}

const SharedWidgetAiInner = ({ data, aiType }: SharedWidgetsAIInnerProps) => {
  const locale = useCurrentUserLocale();

  const orderedData = useMemo(
    () => data && getObjectFromAiType(data, aiType, locale),
    [data, aiType, locale]
  );

  const isAIError =
    data &&
    Object.values(data).length > 0 &&
    Object.values(data).every(
      (subAnalysis) =>
        !subAnalysis ||
        (isFailedSubAnalysisSummary(subAnalysis) &&
          subAnalysis.reason === FailureReasons.NotEnoughData)
    );

  const isEmpty =
    !data || !Object.values(data).some((objectValue) => objectValue !== null);

  return (
    <Root>
      <Empty empty={isAIError || isEmpty}>
        {orderedData &&
          map(Object.entries(orderedData), ([entry, value]) => {
            if (value === null) return;
            const escapedHashtagValue = value.replace(/(^|\s)(#)/g, "$1\\$2");
            return (
              <>
                <Title>
                  {tr(locale, "CUSTOM_PAGES::EDITOR::CARD_LIBRARY::" + entry)}
                </Title>
                <Markdown md={escapedHashtagValue} />
              </>
            );
          })}
      </Empty>
    </Root>
  );
};

export default SharedWidgetAiInner;
